import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useSimulatorShare = (getAuthHeaders, formData, results, consultantName, consultantNif, consultantBirthDate, consultantEmail = "", consultantFunction = "") => {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState({ name: "", notes: "" });
  const [savingShare, setSavingShare] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const handleSaveAndShare = async () => {
    if (!results) {
      toast.error("Calcule a simulação primeiro");
      return;
    }
    if (!consultantName.trim()) {
      toast.error("Preencha o nome do consultor");
      return;
    }
    const nifVal = (consultantNif || "").trim();
    const emailVal = (consultantEmail || "").trim();
    if (!nifVal && !emailVal) {
      toast.error("Preencha o NIF ou Email do consultor (pelo menos um)");
      return;
    }

    // Block saving simulations with negative margins. Strict: no override
    // available — the user must adjust the simulation until the margin is ≥ 0
    // before persisting it as a candidate proposal.
    const firstResult = results?.results?.[0];
    const marginPct = firstResult?.margin_percentage;
    if (typeof marginPct === "number" && marginPct < 0) {
      toast.error(
        `Não é possível guardar simulações com margem negativa (${marginPct.toFixed(1)}%). Ajusta os valores até obteres margem ≥ 0%.`,
        { duration: 6000 }
      );
      return;
    }

    const customId = nifVal || emailVal;
    setSavingShare(true);
    try {
      const response = await axios.post(`${API}/simulations/save`, {
        name: consultantName,
        nif: nifVal,
        email: emailVal,
        notes: shareData.notes,
        custom_id: customId,
        birth_date: consultantBirthDate,
        client_id: formData?.client_id || "",
        simulation_data: { ...formData, consultant_function: consultantFunction || formData?.consultant_function || "" },
        results: results,
      }, { headers: getAuthHeaders() });

      const proposalUrl = `${window.location.origin}/proposal/${response.data.id}`;
      setShareUrl(proposalUrl);
      const action = response.data.action;
      const matchedBy = response.data.matched_by;
      if (action === "updated") {
        toast.success(
          `Candidato já existente atualizado (match por ${matchedBy === "nif" ? "NIF" : "email"}). Link mantido.`,
          { duration: 5000 }
        );
      } else {
        toast.success("Proposta guardada nos Candidatos!");
      }
    } catch (error) {
      toast.error("Erro ao guardar simulação");
    } finally {
      setSavingShare(false);
    }
  };

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copiado!");
  };

  const closeShareDialog = () => {
    setShowShareDialog(false);
    setShareUrl("");
    setShareData({ name: "", notes: "" });
  };

  // ===== Email dialog =====
  const [emailDialog, setEmailDialog] = useState({ open: false, simulationId: "", to: "", sending: false });
  const openEmailDialog = (simulationId, defaultTo = "") => {
    setEmailDialog({ open: true, simulationId, to: defaultTo, sending: false });
  };
  const closeEmailDialog = () => setEmailDialog((s) => ({ ...s, open: false }));
  const sendEmail = async () => {
    if (!emailDialog.to || !emailDialog.to.includes("@")) {
      toast.error("Indique um email válido");
      return;
    }
    setEmailDialog((s) => ({ ...s, sending: true }));
    try {
      const r = await axios.post(
        `${API}/proposal/${emailDialog.simulationId}/send-email`,
        { to: emailDialog.to },
        { headers: getAuthHeaders() }
      );
      toast.success(`Email preparado (MOCKED). Subject: ${r.data.subject}`, { duration: 6000 });
      closeEmailDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally {
      setEmailDialog((s) => ({ ...s, sending: false }));
    }
  };

  return {
    showShareDialog, setShowShareDialog,
    shareData, setShareData,
    savingShare, shareUrl,
    handleSaveAndShare, copyShareUrl, closeShareDialog,
    // Email
    emailDialog, openEmailDialog, closeEmailDialog, sendEmail,
    setEmailDialog,
  };
};
